import type { PortfolioAdminDetail } from '@repo/api-contracts';
import { Button, FileDropField, Input, Label } from '@/shared/ui';
import { usePortfolioFormState } from '../model';
import { PortfolioCategoryField } from './portfolio-category-field';
import { PortfolioLatestComparison } from './portfolio-latest-comparison';
import { PortfolioLogoField } from './portfolio-logo-field';
import { PortfolioPhotoList } from './portfolio-photo-list';
import { PortfolioProductField } from './portfolio-product-field';

export const PortfolioEditorForm = ({
  initialValue,
  onSaved,
  onCancel,
}: {
  initialValue?: PortfolioAdminDetail;
  onSaved: () => void;
  onCancel?: () => void;
}) => {
  const state = usePortfolioFormState(initialValue, onSaved, onCancel);
  return (
    <form
      aria-label="포트폴리오 정보"
      noValidate
      className="mx-auto flex w-full max-w-4xl flex-col gap-6"
      onSubmit={(event) => {
        if (event.target !== event.currentTarget) return;
        event.preventDefault();
        state.submit();
      }}
    >
      <h1 className="text-xl font-semibold">
        {state.original ? '포트폴리오 수정' : '포트폴리오 등록'}
      </h1>
      {state.save.error ? (
        <p role="alert" className="text-destructive">
          {state.save.error}
        </p>
      ) : null}
      {state.save.isConflict ? (
        <div className="space-y-2">
          <p>최신 내용을 확인한 후 다시 편집해주세요. 작성 중인 내용은 유지됩니다.</p>
          <Button
            type="button"
            variant="outline"
            disabled={state.save.isPending}
            onClick={() => void state.save.recover()}
          >
            최신 내용 확인
          </Button>
        </div>
      ) : null}
      {state.save.isUncertain ? (
        <div className="space-y-2">
          <p role="status">
            등록 결과를 아직 확인하지 못했습니다. 내용 변경을 멈추고 같은 요청으로 재시도하거나
            결과를 확인해주세요.
          </p>
          <div className="flex gap-2">
            <Button type="button" disabled={state.save.isPending} onClick={state.retrySave}>
              같은 요청으로 재시도
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={state.save.isPending}
              onClick={() => void state.save.recover()}
            >
              등록 결과 확인
            </Button>
          </div>
        </div>
      ) : null}
      {state.save.latest ? (
        <PortfolioLatestComparison
          original={state.original}
          draft={state.input}
          draftPreviewUrls={state.photos.map(({ previewUrl }) => previewUrl)}
          draftLogoPreviewUrl={
            state.logoPhoto?.previewUrl ?? (state.keepLogo ? state.logoPreviewUrl : null)
          }
          latest={state.save.latest}
          onReload={state.reloadLatest}
          disabled={state.locked}
        />
      ) : null}
      <fieldset disabled={state.locked} className="flex flex-col gap-6">
        <div className="grid gap-4 sm:grid-cols-2">
          {(
            [
              { key: 'title', label: '제목', max: 100 },
              { key: 'clientName', label: '고객사명', max: 100 },
            ] as const
          ).map(({ key, label, max }) => (
            <div key={key} className="space-y-1.5">
              <Label htmlFor={`portfolio-${key}`}>{label}</Label>
              <Input
                id={`portfolio-${key}`}
                value={state.fields[key]}
                onChange={(event) => state.changeField(key, event.target.value)}
                aria-invalid={Boolean(state.fieldErrors[key])}
                aria-describedby={`portfolio-${key}-help`}
              />
              <p id={`portfolio-${key}-help`} className="text-xs text-muted-foreground">
                {state.counts[key]} / {max}
                {key === 'title' ? ' · 필수' : ' · 선택'}
              </p>
              {state.fieldErrors[key] ? (
                <p className="text-xs text-destructive">{state.fieldErrors[key]}</p>
              ) : null}
            </div>
          ))}
        </div>
        {(
          [
            { key: 'companyIntroduction', label: '회사소개' },
            { key: 'productionBackground', label: '제작배경' },
          ] as const
        ).map(({ key, label }) => (
          <div key={key} className="space-y-1.5">
            <Label htmlFor={`portfolio-${key}`}>{label}</Label>
            <textarea
              id={`portfolio-${key}`}
              rows={5}
              value={state.fields[key]}
              onChange={(event) => state.changeField(key, event.target.value)}
              aria-invalid={Boolean(state.fieldErrors[key])}
              aria-describedby={`portfolio-${key}-help`}
              className="w-full rounded-lg border border-input bg-background p-3 text-sm"
            />
            <p id={`portfolio-${key}-help`} className="text-xs text-muted-foreground">
              {state.counts[key]} / 2000
            </p>
            {state.fieldErrors[key] ? (
              <p className="text-xs text-destructive">{state.fieldErrors[key]}</p>
            ) : null}
          </div>
        ))}
        <section className="space-y-3">
          <Label htmlFor="portfolio-photos">사례 사진</Label>
          <p className="text-sm text-muted-foreground">
            {state.photos.length} / 10장 · 첫 사진이 대표사진입니다. JPEG·PNG·WebP, 파일당 최대
            10MiB.
          </p>
          <FileDropField
            id="portfolio-photos"
            multiple
            accept="image/jpeg,image/png,image/webp"
            disabled={state.photos.length >= 10}
            buttonLabel="사진 선택"
            hint="여러 장을 한 번에 고르거나 여기로 끌어다 놓으세요"
            onFiles={(files) => state.uploadPhotos([...files])}
          />
          {state.photoError ? (
            <p role="alert" className="text-sm text-destructive">
              {state.photoError}
            </p>
          ) : null}
          {state.urlError ? (
            <p role="alert" className="text-sm text-destructive">
              {state.urlError}
            </p>
          ) : null}
          <PortfolioPhotoList
            photos={state.photos}
            disabled={state.locked}
            onMove={state.movePhoto}
            onRepresentative={state.representative}
            onRemove={state.removePhoto}
            onRetry={state.retryPhoto}
            onRefresh={state.refreshPhoto}
          />
          {state.fieldErrors.images ? (
            <p className="text-xs text-destructive">사진을 1~10장 준비해주세요.</p>
          ) : null}
        </section>
        <PortfolioLogoField
          photo={state.logoPhoto}
          existingUrl={state.keepLogo ? state.logoPreviewUrl : null}
          disabled={state.locked}
          onUpload={state.uploadLogo}
          onRemove={state.removeLogo}
          onRetry={state.retryLogo}
          onRefresh={state.refreshLogo}
        />
        <PortfolioCategoryField
          ids={state.categoryIds}
          existingCategories={state.original?.categories}
          disabled={state.locked}
          onChange={state.setCategoryIds}
        />
        {state.fieldErrors.categoryIds ? (
          <p className="text-xs text-destructive">카테고리를 1개 이상 선택해주세요.</p>
        ) : null}
        <PortfolioProductField
          ids={state.productIds}
          existingProducts={state.original?.products}
          disabled={state.locked}
          onChange={state.setProductIds}
        />
        <div className="flex flex-wrap gap-5">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={state.fields.isVisible}
              onChange={(event) => state.changeField('isVisible', event.target.checked)}
            />
            공개
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={state.fields.homeSelected}
              onChange={(event) => state.changeField('homeSelected', event.target.checked)}
            />
            홈에 선택
          </label>
        </div>
      </fieldset>
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          disabled={state.save.isPending}
          onClick={state.cancel}
        >
          취소
        </Button>
        <Button type="submit" disabled={!state.canSave} isLoading={state.save.isPending}>
          저장
        </Button>
      </div>
    </form>
  );
};
